import "./couponForm.css";
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { useGetAllCouponsQuery, useUpdateCouponMutation } from '../../services/couponApi'
import { LocalOfferOutlined } from "@mui/icons-material";
import { AdminPageHeader, AdminCard, AdminInput, AdminSelect, LoadingButton, AdminSkeleton, AdminEmptyState, useToast } from '../../components/ui'

const toDatetimeLocal = (value) => {
  if (!value) return '';
  const date = new Date(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const CouponEdit = () => {
  const { id } = useParams()
  const navigate = useNavigate();
  const { data: coupons, isLoading: couponsLoading, isError } = useGetAllCouponsQuery()
  const [updateCoupon, { isLoading }] = useUpdateCouponMutation()
  const [server_error, setServerError] = useState({})
  const [formData, setFormData] = useState(null)
  const toast = useToast();

  const coupon = coupons?.find((c) => String(c.id) === id)

  useEffect(() => {
    if (coupon) {
      setFormData({
        code: coupon.code,
        discount_type: coupon.discount_type,
        value: coupon.value,
        min_order_amount: coupon.min_order_amount,
        max_uses: coupon.max_uses ?? '',
        valid_until: toDatetimeLocal(coupon.valid_until),
        is_active: coupon.is_active,
      })
    }
  }, [coupon])

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const actualData = {
      code: formData.code,
      discount_type: formData.discount_type,
      value: formData.value,
      min_order_amount: formData.min_order_amount || 0,
      max_uses: formData.max_uses || null,
      valid_until: formData.valid_until || null,
      is_active: formData.is_active,
    };

    const res = await updateCoupon({ id, data: actualData });
    if (res.error) {
      setServerError(res.error.data?.errors || {});
      toast.error("Could not update this coupon. Check the fields below.");
    }
    if (res.data) {
      toast.success("Coupon updated successfully.");
      setTimeout(() => navigate('/admin/coupons'), 1200);
    }
  };

  const breadcrumbs = [{ label: "Dashboard", to: "/admin/dashboard" }, { label: "Coupons", to: "/admin/coupons" }, { label: "Edit" }];

  if (couponsLoading || !formData) {
    return (
      <div>
        <AdminPageHeader title="Edit Coupon" breadcrumbs={breadcrumbs} />
        <AdminCard><AdminSkeleton variant="card" height={200} /></AdminCard>
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <AdminPageHeader title="Edit Coupon" breadcrumbs={breadcrumbs} />
        <AdminEmptyState icon={<LocalOfferOutlined />} title="Couldn't load this coupon" description="Something went wrong. Try refreshing the page." />
      </div>
    );
  }

  return (
    <div>
      <AdminPageHeader title="Edit Coupon" breadcrumbs={breadcrumbs} />
      <AdminCard className="couponForm">
        <form onSubmit={handleSubmit}>
          <AdminInput label="Coupon Code" name="code" value={formData.code || ""} onChange={handleChange} error={server_error.code?.[0]} required />
          <AdminSelect label="Discount Type" name="discount_type" value={formData.discount_type} onChange={handleChange}>
            <option value="PERCENT">Percentage</option>
            <option value="FLAT">Flat Amount</option>
          </AdminSelect>
          <AdminInput label={`Value (${formData.discount_type === 'PERCENT' ? '%' : '₹'})`} type="number" step="0.01" name="value" value={formData.value || ""} onChange={handleChange} error={server_error.value?.[0]} required />
          <AdminInput label="Minimum Order Amount" type="number" step="0.01" name="min_order_amount" value={formData.min_order_amount || ""} onChange={handleChange} />
          <AdminInput label="Max Uses (blank = unlimited)" type="number" name="max_uses" value={formData.max_uses || ""} onChange={handleChange} />
          <AdminInput label="Valid Until" type="datetime-local" name="valid_until" value={formData.valid_until || ""} onChange={handleChange} />
          <label className="couponForm__checkbox">
            <input type="checkbox" name="is_active" checked={!!formData.is_active} onChange={handleChange} /> Active
          </label>
          <div className="couponForm__actions">
            <LoadingButton type="submit" loading={isLoading} loadingLabel="Saving...">Update Coupon</LoadingButton>
          </div>
        </form>
      </AdminCard>
    </div>
  );
}

export default CouponEdit
